import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useQueryClient } from 'react-query';
import { stateToHTML } from 'draft-js-export-html';
import {
  localFilesSelector,
  selectedQuestionSelector,
} from '../../store/selectors';
import useQuestionsQueryParams from '../../hooks/useQuestionsQueryParams';
import { useValueRef } from '../../../../hooks/useValueRef';
import useCompanyDefaultSignature from '../../queries/useCompanyDefaultSignature';
import { questionsQueryKeys } from '../../queries/questionsQueryKeys';
import {
  resetAreaEditorStates,
  setIsHtml,
  setLocalFiles,
} from '../../store/actions';
import useAddMsgAttachmentsMutation from '../../mutations/useAddMsgAttachmentsMutation';
import useAddMessageMutation from '../../mutations/useAddMessageMutation';

const SubmitContext = createContext();
const useSubmit = () => useContext(SubmitContext);

const getMessage = ({ isHtml, editorState, messageAreaState }) =>
  isHtml
    ? stateToHTML(editorState.getCurrentContent())
        .replace(/\n/g, '')
        .replace(/&nbsp;/g, '')
        .replace(/<p>(<br\/?>)<\/p>/g, '$1')
    : messageAreaState;

const getSubmitData = (state) => ({
  questionId: state.selectedQuestion?.id,
  isHtml: state.isHtml,
  message: getMessage(state),
});

const SubmitProvider = ({ children, onAddMsgSuccess }) => {
  const { getState, dispatch } = useStore();

  const selectedQuestion = useSelector(selectedQuestionSelector);
  const queryClient = useQueryClient();
  const questionsQueryParams = useQuestionsQueryParams();
  const onAddMsgSuccessRef = useValueRef(onAddMsgSuccess);

  const { data: companyDefaultSignature } = useCompanyDefaultSignature(
    selectedQuestion?.id,
    { enabled: Boolean(selectedQuestion?.id), staleTime: Infinity }
  );

  const valuesRef = useValueRef({
    questionsQueryParams,
    selectedQuestion,
    companyDefaultSignature,
  });

  const invalidateMessages = useCallback(async () => {
    const selectedQuestion = valuesRef.current.selectedQuestion;

    await queryClient.invalidateQueries(
      questionsQueryKeys.questionMessages(selectedQuestion?.id)
    );

    if (typeof onAddMsgSuccessRef.current === 'function') {
      onAddMsgSuccessRef.current();
    }
  }, [queryClient, valuesRef, onAddMsgSuccessRef]);

  const invalidateQuestions = useCallback(() => {
    const params = valuesRef.current.questionsQueryParams;
    queryClient.invalidateQueries(questionsQueryKeys.questions(params));
  }, [queryClient, valuesRef]);

  const handleAddFilesSuccess = useCallback(() => {
    dispatch(setLocalFiles([]));
    invalidateMessages();
  }, [dispatch, invalidateMessages]);

  const { mutate: addAttachments, isLoading: isAddAttachmentsLoading } =
    useAddMsgAttachmentsMutation({ onSuccess: handleAddFilesSuccess });

  const handleAddMessageSuccess = useCallback(
    (data) => {
      if (data.id) {
        dispatch(resetAreaEditorStates());
        invalidateQuestions();

        const files = localFilesSelector(getState());
        files.length
          ? addAttachments({ msgId: data.id, files })
          : invalidateMessages();
      }
    },
    [
      dispatch,
      invalidateQuestions,
      getState,
      addAttachments,
      invalidateMessages,
    ]
  );

  const { mutateAsync: addMessage, isLoading: isAddMessageLoading } =
    useAddMessageMutation({ onSuccess: handleAddMessageSuccess });

  const handleSubmit = useCallback(async () => {
    const state = getState().questionsStore;

    const data = getSubmitData(state);

    const companySignature =
      valuesRef.current.companyDefaultSignature !== undefined
        ? valuesRef.current.companyDefaultSignature
        : true;

    await addMessage({
      ...data,
      companySignature,
    });
    dispatch(setIsHtml(false));
  }, [getState, valuesRef, addMessage, dispatch]);

  const loading = isAddMessageLoading || isAddAttachmentsLoading;

  const value = useMemo(
    () => ({ loading, onSubmit: handleSubmit }),
    [loading, handleSubmit]
  );

  return (
    <SubmitContext.Provider value={value}>{children}</SubmitContext.Provider>
  );
};

export { SubmitProvider, useSubmit };
